import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { getErrorMessage } from '../lib/utils';
import { onboardingAPI, employeeAPI } from '../services/api';
import { Users, FileText, CheckCircle, Upload, ClipboardList, Play, ShieldCheck } from 'lucide-react';
import { useTableControls } from '../hooks/useTableControls';
import { TableControls } from '../components/ui/table-controls';

const OnboardingManagement = ({ currentTab = 'employees' }) => {
  const navigate = useNavigate();
  const [documentTypes, setDocumentTypes] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeeOnboarding, setEmployeeOnboarding] = useState(null);
  const [employeeDocuments, setEmployeeDocuments] = useState([]);
  const [showUploadDoc, setShowUploadDoc] = useState(false);
  const [verificationQueue, setVerificationQueue] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  const [queueStatus, setQueueStatus] = useState('pending');
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const verificationQueueTable = useTableControls(verificationQueue, {
    initialSortKey: 'id',
    initialSortDirection: 'desc',
  });
  
  const [uploadData, setUploadData] = useState({
    document_type_id: '',
    file: null,
    expiry_date: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [typesRes, checklistsRes, employeesRes, queueRes] = await Promise.all([
        onboardingAPI.getDocumentTypes(),
        onboardingAPI.getChecklists(),
        employeeAPI.list(),
        onboardingAPI.getDocumentVerificationQueue({ status: queueStatus, page: 1, page_size: 100 }),
      ]);
      setDocumentTypes(typesRes.data);
      setChecklists(checklistsRes.data);
      setEmployees(employeesRes.data);
      setVerificationQueue(queueRes.data?.items || []);
      setQueueTotal(queueRes.data?.total || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [queueStatus]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshVerificationQueue = async (status = queueStatus) => {
    try {
      const response = await onboardingAPI.getDocumentVerificationQueue({ status, page: 1, page_size: 100 });
      setVerificationQueue(response.data?.items || []);
      setQueueTotal(response.data?.total || 0);
    } catch (error) {
      console.error('Error loading verification queue:', error);
    }
  };

  const loadEmployeeDetails = async (emp) => {
    setSelectedEmployee(emp);
    try {
      const [onboardingRes, docsRes] = await Promise.all([
        onboardingAPI.getEmployeeOnboarding(emp.employee_id),
        onboardingAPI.getEmployeeDocuments(emp.employee_id)
      ]);
      setEmployeeOnboarding(onboardingRes.data);
      setEmployeeDocuments(docsRes.data);
    } catch (error) {
      console.error('Error loading employee details:', error);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedEmployee || !uploadData.file || !uploadData.document_type_id) {
      toast.error('Please select a document type and file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadData.file);

    try {
      await onboardingAPI.uploadDocument(
        selectedEmployee.employee_id,
        formData,
        {
          document_type_id: uploadData.document_type_id,
          expiry_date: uploadData.expiry_date || undefined
        }
      );
      toast.success('Document uploaded successfully');
      setShowUploadDoc(false);
      setUploadData({ document_type_id: '', file: null, expiry_date: '' });
      loadEmployeeDetails(selectedEmployee);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload document'));
    }
  };

  const handleVerifyDocument = async (docId) => {
    if (!selectedEmployee) return;
    try {
      await onboardingAPI.verifyDocument(selectedEmployee.employee_id, docId);
      toast.success('Document verified');
      loadEmployeeDetails(selectedEmployee);
    } catch (error) {
      toast.error('Failed to verify document');
    }
  };

  const handleBulkReview = async (action) => {
    if (selectedDocIds.length === 0) {
      toast.error('Select at least one document');
      return;
    }
    try {
      await onboardingAPI.bulkVerifyDocuments({
        document_ids: selectedDocIds,
        action,
      });
      toast.success(`Bulk ${action} completed`);
      setSelectedDocIds([]);
      refreshVerificationQueue(queueStatus);
      if (selectedEmployee) {
        loadEmployeeDetails(selectedEmployee);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to ${action} selected documents`));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-[hsl(var(--chart-4))]/10 text-[hsl(var(--chart-4))]',
      in_progress: 'bg-primary/10 text-primary',
      completed: 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))]',
      not_started: 'bg-muted text-muted-foreground'
    };
    return <Badge className={colors[status] || 'bg-muted text-muted-foreground'}>{status?.replace('_', ' ')}</Badge>;
  };

  const calculateProgress = (onboarding) => {
    if (!onboarding || onboarding.status === 'not_started') return 0;
    const completed = onboarding.completed_items?.length || 0;
    const pending = onboarding.pending_items?.length || 0;
    const total = completed + pending;
    return total > 0 ? Math.round((completed / total) * 100) : 0;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-management">
        {/* Employee Onboarding Tab */}
        {currentTab === 'employees' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Employee List */}
            <Card className="col-span-1 shadow-md border-0 h-full">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" />
                  Select Employee
                </CardTitle>
              </CardHeader>
              <CardContent className="max-h-[600px] overflow-y-auto p-0">
                <div className="divide-y divide-border">
                  {employees.map((emp, index) => (
                    <div
                      key={emp.employee_id}
                      className={`p-4 cursor-pointer transition-all duration-200 hover:bg-accent/50 animate-slide-up ${
                        selectedEmployee?.employee_id === emp.employee_id 
                          ? 'bg-primary/5 border-l-4 border-l-primary pl-3' 
                          : 'border-l-4 border-l-transparent'
                      }`}
                      style={{ animationDelay: `${index * 0.05}s` }}
                      onClick={() => loadEmployeeDetails(emp)}
                      data-testid={`employee-select-${emp.employee_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          selectedEmployee?.employee_id === emp.employee_id 
                            ? 'bg-primary text-primary-foreground' 
                            : 'bg-muted text-muted-foreground'
                        }`}>
                          {emp.first_name.charAt(0)}{emp.last_name.charAt(0)}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${
                            selectedEmployee?.employee_id === emp.employee_id ? 'text-primary' : 'text-foreground'
                          }`}>
                            {emp.first_name} {emp.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">{emp.employee_id}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Employee Onboarding Details */}
            <Card className="col-span-1 md:col-span-2 shadow-md border-0">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClipboardList className="h-5 w-5 text-primary" />
                  {selectedEmployee 
                    ? `${selectedEmployee.first_name} ${selectedEmployee.last_name}'s Onboarding`
                    : 'Select an Employee'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {selectedEmployee ? (
                  <div className="space-y-6 animate-fade-in">
                    {/* Onboarding Status */}
                    <div className="p-5 border border-border rounded-xl bg-card shadow-sm hover:shadow transition-shadow">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-foreground">Onboarding Status</h4>
                        {getStatusBadge(employeeOnboarding?.status)}
                      </div>
                      
                      {employeeOnboarding?.status === 'not_started' ? (
                        <div className="text-center py-6">
                          <p className="text-muted-foreground mb-4 text-sm">Onboarding has not been initiated for this employee.</p>
                          <Button 
                            onClick={() => navigate(`/admin/onboarding-wizard/${selectedEmployee.employee_id}`)}
                            data-testid="start-onboarding-btn"
                            className="bg-primary hover:bg-primary/90 shadow-sm active:scale-95 transition-all"
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Open Wizard
                          </Button>
                        </div>
                      ) : (
                        <div>
                          <div className="mb-4 flex justify-end">
                            <Button variant="outline" size="sm" onClick={() => navigate(`/admin/onboarding-wizard/${selectedEmployee.employee_id}`)}>
                              Open Wizard
                            </Button>
                          </div>
                          <div className="flex justify-between text-sm mb-2 font-medium text-foreground">
                            <span>Progress</span>
                            <span>{calculateProgress(employeeOnboarding)}%</span>
                          </div>
                          <Progress value={calculateProgress(employeeOnboarding)} className="h-2.5 bg-muted" indicatorClassName="bg-primary" />
                          {employeeOnboarding?.target_completion_date && (
                            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Target Completion: {employeeOnboarding.target_completion_date}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Tasks */}
                    {employeeOnboarding?.tasks?.length > 0 && (
                      <div className="p-5 border border-border rounded-xl bg-card shadow-sm hover:shadow transition-shadow">
                        <h4 className="font-semibold mb-4 flex items-center gap-2 text-foreground">
                          <ClipboardList className="h-4 w-4 text-primary" />
                          Onboarding Tasks
                        </h4>
                        <div className="space-y-3">
                          {employeeOnboarding.tasks.map((task, index) => (
                            <div 
                              key={task.id} 
                              className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg border border-border hover:border-primary/30 hover:bg-card transition-all duration-200 group animate-slide-up"
                              style={{ animationDelay: `${index * 0.05}s` }}
                            >
                              <CheckCircle className={`h-5 w-5 flex-shrink-0 transition-colors ${
                              task.status === 'completed' ? 'text-[hsl(var(--chart-2))]' : 'text-muted-foreground'
                            }`} />
                              <span className={`text-sm ${task.status === 'completed' ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                                {task.task_name}
                              </span>
                              <Badge variant="outline" className="ml-auto text-[10px] bg-card text-muted-foreground border-border">{task.category}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Documents */}
                    <div className="p-5 border border-border rounded-xl bg-card shadow-sm hover:shadow transition-shadow">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold flex items-center gap-2 text-foreground">
                          <FileText className="h-4 w-4 text-primary" />
                          Documents ({employeeDocuments.length})
                        </h4>
                        <Button 
                          size="sm" 
                          onClick={() => setShowUploadDoc(true)} 
                          data-testid="upload-doc-btn"
                          className="bg-card text-foreground border border-border hover:bg-accent shadow-sm hover:shadow active:scale-95 transition-all"
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Upload
                        </Button>
                      </div>
                      {employeeDocuments.length === 0 ? (
                        <div className="text-center py-8 bg-muted/30 rounded-lg border border-dashed border-border">
                          <FileText className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No documents uploaded yet</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {employeeDocuments.map((doc, index) => (
                            <div 
                              key={doc.id} 
                              className="flex items-center justify-between p-3 bg-card border border-border rounded-lg hover:border-primary/30 hover:shadow-sm transition-all duration-200 animate-slide-up"
                              style={{ animationDelay: `${index * 0.1}s` }}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                                  <FileText className="h-5 w-5" />
                                </div>
                                <div>
                                  <p className="font-medium text-sm text-foreground">{doc.document_name}</p>
                                  <p className="text-xs text-muted-foreground">{doc.document_type}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {doc.verified ? (
                                  <Badge className="bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))] border-[hsl(var(--chart-2))]/20 hover:bg-[hsl(var(--chart-2))]/20">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Verified
                                  </Badge>
                                ) : (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="h-8 text-xs border-primary/30 text-primary hover:bg-primary/5 hover:text-primary"
                                    onClick={() => handleVerifyDocument(doc.id)}
                                  >
                                    Verify
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    Select an employee to view their onboarding status
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Document Types Tab */}
        {currentTab === 'documents' && (
          <div className="space-y-6 animate-fade-in">
            <Card className="shadow-md border-0">
              <CardHeader className="bg-muted/30 border-b border-border pb-4">
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  Bulk Verification Queue
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  <Select value={queueStatus} onValueChange={(value) => { setQueueStatus(value); refreshVerificationQueue(value); }}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="verified">Verified</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => refreshVerificationQueue(queueStatus)}>Refresh</Button>
                  <Badge variant="outline">{queueTotal} records</Badge>
                  <Button size="sm" onClick={() => handleBulkReview('verify')} disabled={selectedDocIds.length === 0}>
                    Verify Selected
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleBulkReview('reject')} disabled={selectedDocIds.length === 0}>
                    Reject Selected
                  </Button>
                </div>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-hr-sm">
                    <thead>
                      <tr className="border-b bg-muted/40">
                        <th className="p-3 text-left">Select</th>
                        <th className="p-3 text-left">Employee</th>
                        <th className="p-3 text-left">Document</th>
                        <th className="p-3 text-left">Type</th>
                        <th className="p-3 text-left">Expiry</th>
                        <th className="p-3 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {verificationQueueTable.data.map((doc) => (
                        <tr key={doc.id} className="border-b hover:bg-accent/30">
                          <td className="p-3">
                            <input
                              type="checkbox"
                              checked={selectedDocIds.includes(doc.id)}
                              onChange={(e) => {
                                setSelectedDocIds((prev) =>
                                  e.target.checked ? [...prev, doc.id] : prev.filter((id) => id !== doc.id)
                                );
                              }}
                            />
                          </td>
                          <td className="p-3">{doc.employee_id}</td>
                          <td className="p-3">{doc.document_name}</td>
                          <td className="p-3">{doc.document_type}</td>
                          <td className="p-3">
                            {doc.expiry_date || '-'}
                            {doc.days_to_expiry !== null && doc.days_to_expiry !== undefined && (
                              <span className="ml-2 text-hr-xs text-muted-foreground">({doc.days_to_expiry}d)</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge variant={
                              doc.verification_status === 'verified' ? 'success' :
                              doc.verification_status === 'rejected' ? 'danger' : 'warning'
                            }>
                              {(doc.verification_status || 'pending').toUpperCase()}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                      {verificationQueueTable.data.length === 0 && (
                        <tr>
                          <td colSpan={6} className="p-4 text-center text-muted-foreground">No queue items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pt-3">
                  <TableControls
                    totalRows={verificationQueueTable.totalRows}
                    page={verificationQueueTable.page}
                    totalPages={verificationQueueTable.totalPages}
                    pageSize={verificationQueueTable.pageSize}
                    pageSizes={verificationQueueTable.pageSizes}
                    onPageSizeChange={verificationQueueTable.onPageSizeChange}
                    onPrevious={verificationQueueTable.goToPreviousPage}
                    onNext={verificationQueueTable.goToNextPage}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-md border-0">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                Required Document Types
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {documentTypes.map((dt, index) => (
                  <div 
                    key={dt.id} 
                    className="p-4 border border-border rounded-lg hover:border-primary/30 hover:shadow-md transition-all duration-200 animate-slide-up bg-card group"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`doc-type-${dt.id}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-1.5 bg-primary/10 rounded-md group-hover:bg-primary/20 transition-colors">
                        <FileText className="h-4 w-4 text-primary" />
                      </div>
                      <span className="font-semibold text-foreground">{dt.name}</span>
                      {dt.is_mandatory && (
                        <Badge variant="destructive" className="ml-auto text-xs shadow-sm">Required</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{dt.description}</p>
                    <p className="text-hr-xs text-muted-foreground mb-2">
                      Roles: {(dt.required_roles && dt.required_roles.length > 0) ? dt.required_roles.join(', ') : 'All'}
                    </p>
                    <p className="text-hr-xs text-muted-foreground mb-2">
                      Expiry Rule: {dt.requires_expiry ? `Required • Alert ${dt.expiry_alert_days || 30}d` : 'Not Required'}
                    </p>
                    <Badge variant="outline" className="text-xs bg-muted/50 text-muted-foreground border-border group-hover:border-primary/30 transition-colors">{dt.category}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
            </Card>
          </div>
        )}

        {/* Checklists Tab */}
        {currentTab === 'checklists' && (
          <Card className="shadow-md border-0 animate-fade-in">
            <CardHeader className="bg-muted/30 border-b border-border pb-4">
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-primary" />
                Onboarding Checklists
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {checklists.map((cl, index) => (
                  <div 
                    key={cl.id} 
                    className="p-5 border border-border rounded-xl bg-card hover:border-primary/30 hover:shadow-md transition-all duration-200 animate-slide-up group"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`checklist-${cl.id}`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-primary/10 rounded-lg group-hover:bg-primary/20 transition-colors">
                        <ClipboardList className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-foreground">{cl.name}</h4>
                        <p className="text-xs text-muted-foreground">
                          {cl.items?.length || 0} items
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4">{cl.description}</p>
                    {cl.items && (
                      <div className="space-y-2 bg-muted/30 p-3 rounded-lg border border-border">
                        {cl.items.slice(0, 5).map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-foreground">
                            <CheckCircle className="h-3.5 w-3.5 text-primary/70" />
                            <span className="truncate">{item.name}</span>
                            <Badge variant="outline" className="text-[10px] ml-auto bg-card border-border text-muted-foreground">{item.assigned_to}</Badge>
                          </div>
                        ))}
                        {cl.items.length > 5 && (
                          <p className="text-xs text-primary font-medium pl-6 pt-1">+ {cl.items.length - 5} more items</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Upload Document Dialog */}
      <Dialog open={showUploadDoc} onOpenChange={setShowUploadDoc}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Document Type</Label>
              <Select
                value={uploadData.document_type_id}
                onValueChange={(v) => setUploadData({...uploadData, document_type_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.id} value={dt.id.toString()}>{dt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>File</Label>
              <Input
                type="file"
                onChange={(e) => setUploadData({...uploadData, file: e.target.files[0]})}
                data-testid="document-file-input"
              />
            </div>
            <div>
              <Label>Expiry Date (optional)</Label>
              <Input
                type="date"
                value={uploadData.expiry_date}
                onChange={(e) => setUploadData({...uploadData, expiry_date: e.target.value})}
              />
            </div>
            <Button 
              onClick={handleUploadDocument} 
              className="w-full bg-primary hover:bg-primary/90 active:scale-95 transition-all shadow-sm" 
              data-testid="submit-upload-btn"
            >
              Upload Document
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OnboardingManagement;
